import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Pill } from "@/components/perf";
import Avatar from "@/components/Avatar";
import CoachPhotoInput from "@/components/CoachPhotoInput";
import ExpandableText from "@/components/ExpandableText";
import { ArrowLeft, GraduationCap, MapPin, Building2, Medal, Star, X, Pencil } from "lucide-react";

export default function CoachesLounge() {
  const [coaches, setCoaches] = useState(null);
  const [reviewsFor, setReviewsFor] = useState(null); // coach object
  const [editingCard, setEditingCard] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/coaches/lounge").then(({ data }) => setCoaches(data)).catch(() => setCoaches([]));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="coaches-lounge-page">
      <button data-testid="lounge-back-btn" onClick={() => navigate("/book")}
        className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-pb-red transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Booking
      </button>
      <PageHeader kicker="Meet Your Coaching Staff" title="Coaches Lounge"
        subtitle="Get to know the coaches behind every Bullpen session — their specialties, experience, and home courts. Book their sessions from the Book a Session page." />

      {coaches === null ? (
        <div className="p-8 text-sm text-zinc-500 animate-pulse">Loading coaches…</div>
      ) : coaches.length === 0 ? (
        <div className="border border-pb-border bg-pb-surface p-8 text-sm text-zinc-500" data-testid="lounge-empty">
          No coaches listed yet — check back soon.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="lounge-grid">
          {coaches.map((c) => (
            <div key={c.id} data-testid={`lounge-coach-${c.id}`}
              className="bg-pb-surface border border-pb-border p-6 hover:border-pb-red transition-all hover:-translate-y-1 animate-fade-up">
              <div className="flex items-center gap-4 mb-4">
                <Avatar src={c.avatar} name={c.name} size={64} testId={`lounge-avatar-${c.id}`} />
                <div className="min-w-0">
                  <div className="font-display font-black uppercase text-xl leading-tight">{c.name}</div>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    {c.rating_count > 0 && (
                      <span data-testid={`lounge-rating-${c.id}`} className="flex items-center gap-1 text-sm font-bold text-amber-400">
                        <Star className="w-4 h-4 fill-amber-400" /> {c.rating_avg.toFixed(1)}
                        <span className="text-zinc-500 font-normal text-xs">({c.rating_count})</span>
                      </span>
                    )}
                    {c.specialty && <Pill color="#A3E635">{c.specialty}</Pill>}
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm mb-3">
                {c.experience && (
                  <div className="flex items-start gap-2 text-zinc-300">
                    <Medal className="w-4 h-4 text-pb-cyan mt-0.5 shrink-0" /> {c.experience}
                  </div>
                )}
                {c.facility && (
                  <div className="flex items-start gap-2 text-zinc-400">
                    <Building2 className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" /> {c.facility}
                  </div>
                )}
                {c.location && (
                  <div className="flex items-start gap-2 text-zinc-400">
                    <MapPin className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" /> {c.location}
                  </div>
                )}
              </div>
              {c.bio && (
                <div className="border-t border-pb-border/60 pt-3">
                  <ExpandableText text={c.bio} lines={3} testid={`lounge-bio-${c.id}`} className="text-sm text-zinc-400 italic" />
                </div>
              )}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button data-testid={`lounge-book-${c.id}`} onClick={() => navigate("/book")}
                  className="flex items-center justify-center gap-1.5 border border-pb-red/60 text-pb-red hover:bg-pb-red hover:text-black font-bold uppercase tracking-widest text-xs py-2.5 transition-all">
                  <GraduationCap className="w-4 h-4" /> Book
                </button>
                <button data-testid={`lounge-reviews-${c.id}`} onClick={() => setReviewsFor(c)}
                  className="flex items-center justify-center gap-1.5 border border-pb-border text-zinc-300 hover:border-amber-400 hover:text-amber-400 font-bold uppercase tracking-widest text-xs py-2.5 transition-colors">
                  <Star className="w-4 h-4" /> Reviews
                </button>
              </div>
              {c.mine && (
                <button data-testid={`lounge-edit-card-${c.id}`} onClick={() => setEditingCard(c)}
                  className="mt-2 w-full flex items-center justify-center gap-1.5 border border-pb-cyan/60 text-pb-cyan hover:bg-pb-cyan/10 font-bold uppercase tracking-widest text-xs py-2.5 transition-colors">
                  <Pencil className="w-3.5 h-3.5" /> Edit My Card
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {reviewsFor && <ReviewsModal coach={reviewsFor} onClose={() => setReviewsFor(null)} onSaved={load} />}
      {editingCard && <EditCardModal coach={editingCard} onClose={() => setEditingCard(null)} onSaved={() => { setEditingCard(null); load(); }} />}
    </div>
  );
}

function EditCardModal({ coach, onClose, onSaved }) {
  const [form, setForm] = useState({
    specialty: coach.specialty || "", experience: coach.experience || "",
    facility: coach.facility || "", location: coach.location || "",
    bio: coach.bio || "", photo: coach.avatar || "",
  });
  const [saving, setSaving] = useState(false);
  const input = "w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan text-sm";

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/coach/my-card", form);
      toast.success("Your coach card is updated!");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save your card"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-pb-surface border border-pb-cyan w-full max-w-md max-h-[90vh] overflow-y-auto p-5 animate-fade-up"
        onClick={(e) => e.stopPropagation()} data-testid="edit-my-card-modal">
        <div className="flex items-center justify-between mb-4">
          <span className="font-display font-bold text-lg uppercase">Edit My Coach Card</span>
          <button data-testid="edit-my-card-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Photo</label>
            <CoachPhotoInput value={form.photo} name={coach.name} onChange={(v) => setForm({ ...form, photo: v })} testId="my-card-photo" />
          </div>
          {[["specialty", "Specialty"], ["experience", "Experience"], ["facility", "Facility"], ["location", "Location"]].map(([k, l]) => (
            <div key={k}>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{l}</label>
              <input data-testid={`my-card-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={input} />
            </div>
          ))}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Bio (shown to members)</label>
            <textarea data-testid="my-card-bio" rows={3} maxLength={500} value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })} className={`${input} resize-none`} />
          </div>
          <button data-testid="my-card-save" onClick={save} disabled={saving}
            className="w-full bg-pb-cyan text-black font-bold uppercase tracking-widest py-2.5 hover:opacity-90 active:scale-95 transition-all disabled:opacity-50">
            {saving ? "Saving…" : "Save My Card"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewsModal({ coach, onClose, onSaved }) {
  const [reviews, setReviews] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/coaches/${coach.id}/reviews`).then(({ data }) => {
    setReviews(data);
    const mine = data.find((r) => r.mine);
    if (mine) { setRating(mine.rating); setComment(mine.comment || ""); }
  }).catch(() => setReviews([]));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const submit = async () => {
    if (!rating) return toast.error("Pick a star rating first");
    setSaving(true);
    try {
      await api.post(`/coaches/${coach.id}/reviews`, { rating, comment: comment || null });
      toast.success(`Thanks — your ${rating}★ review for ${coach.name} is saved!`);
      await load();
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save review"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-pb-surface border border-pb-border w-full max-w-md max-h-[85vh] flex flex-col animate-fade-up"
        onClick={(e) => e.stopPropagation()} data-testid="coach-reviews-modal">
        <div className="flex items-center justify-between p-5 border-b border-pb-border shrink-0">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400" />
            <h3 className="font-display font-black text-xl uppercase">{coach.name} — Reviews</h3>
          </div>
          <button data-testid="reviews-modal-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 border-b border-pb-border shrink-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Rate this coach</div>
          <div className="flex items-center gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                className="p-0.5 transition-transform hover:scale-110">
                <Star className={`w-7 h-7 ${(hover || rating) >= n ? "text-amber-400 fill-amber-400" : "text-zinc-700"}`} />
              </button>
            ))}
          </div>
          <textarea data-testid="review-comment" value={comment} rows={2} maxLength={500}
            placeholder="What was your session like? (optional)"
            onChange={(e) => setComment(e.target.value)}
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red resize-none" />
          <button data-testid="review-submit" onClick={submit} disabled={saving}
            className="mt-2 w-full bg-pb-red text-black font-bold uppercase tracking-widest py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
            {saving ? "Saving…" : "Submit Review"}
          </button>
          <p className="text-[10px] text-zinc-600 mt-2">You can rate a coach after completing a confirmed session with them. Re-submitting updates your review.</p>
        </div>

        <div className="overflow-y-auto flex-1 divide-y divide-pb-border/40">
          {reviews === null ? (
            <div className="p-5 text-sm text-zinc-500 animate-pulse">Loading reviews…</div>
          ) : reviews.length === 0 ? (
            <div className="p-5 text-sm text-zinc-600" data-testid="reviews-empty">No reviews yet — be the first!</div>
          ) : reviews.map((r) => (
            <div key={r.id} className="p-4" data-testid="review-row">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-sm">{r.user_name} {r.mine && <span className="text-[10px] text-pb-cyan uppercase">(you)</span>}</span>
                <span className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-3.5 h-3.5 ${r.rating >= n ? "text-amber-400 fill-amber-400" : "text-zinc-700"}`} />
                  ))}
                </span>
              </div>
              {r.comment && <p className="text-sm text-zinc-400">{r.comment}</p>}
              <div className="text-[10px] text-zinc-600 mt-1">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
